import { useContext, useEffect, useState } from "react";
import { MapViewerContext } from "src/contexts/MapViewerContext";
import { Image as ImageLayer } from "ol/layer";
import Overlay from "ol/Overlay";
import { toLonLat } from "ol/proj";
import ImageWMSSource from "ol/source/ImageWMS";
import { Autocomplete, TextField } from "@mui/material"; // Import Material-UI

const LayerStunting = () => {
  const { map } = useContext(MapViewerContext);
  const [layerNames, setLayerNames] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // State untuk kata yang diketik pengguna
  const [filteredLayers, setFilteredLayers] = useState([]); // State untuk hasil pencarian saran
  const [activeLayer, setActiveLayer] = useState(null); // Layer aktif yang dipilih

  const wmsUrl = "https://cirebonkab.ina-sdi.or.id/geoserver/ADMIN/wms";

  // Fungsi untuk mengambil daftar layer dari GetCapabilities
  const fetchLayers = async () => {
    const capabilitiesUrl = `${wmsUrl}?service=WMS&version=1.1.1&request=GetCapabilities`;

    try {
      const response = await fetch(capabilitiesUrl);
      const text = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, "text/xml");

      const layers = Array.from(xmlDoc.querySelectorAll("Layer > Name"));
      const layerList = layers.map((layer) => layer.textContent);
      setLayerNames(layerList); // Set daftar layer ke state
    } catch (error) {
      console.error("Error fetching WMS layers:", error);
    }
  };

  useEffect(() => {
    fetchLayers();
  }, []);

  // Update daftar saran ketika pengguna mengetik di kolom pencarian
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredLayers([]);
    } else {
      const results = layerNames.filter((layer) =>
        layer.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredLayers(results);
    }
  }, [searchTerm, layerNames]);

  // Fungsi untuk menambahkan layer yang dipilih ke peta
  const addLayerToMap = (layerName) => {
    if (!map || !layerName) return;

    // Bersihkan layer sebelumnya jika ada
    if (activeLayer) {
      const layers = map.getLayers().getArray();
      layers.forEach((layer) => {
        if (layer.get("id") === activeLayer) {
          map.removeLayer(layer);
        }
      });
    }

    const wmsSource = new ImageWMSSource({
      url: wmsUrl,
      params: { LAYERS: layerName, FORMAT: "image/png", VERSION: "1.1.1" },
      ratio: 1,
      serverType: "geoserver",
      crossOrigin: "anonymous",
    });

    const wmsLayer = new ImageLayer({
      source: wmsSource,
      zIndex: 0,
    });
    wmsLayer.set("id", layerName);

    map.addLayer(wmsLayer);
    setActiveLayer(layerName); // Set layer yang dipilih sebagai aktif
    setFilteredLayers([]); // Kosongkan saran setelah dipilih
    setSearchTerm(""); // Bersihkan kolom pencarian
  };

  // Membuat popup
  useEffect(() => {
    if (!map) return;

    const popupElement = document.createElement("div");
    popupElement.className = "ol-popup";
    popupElement.style.position = "fixed";
    popupElement.style.width = "250px";
    popupElement.style.padding = "15px";
    popupElement.style.backgroundColor = "white";
    popupElement.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.2)";
    popupElement.style.zIndex = "1000";
    popupElement.style.overflowY = "auto";
    popupElement.style.maxHeight = "300px";

    const closer = document.createElement("span");
    closer.innerHTML = "&#10005;";
    closer.style.position = "sticky";
    closer.style.top = "0";
    closer.style.right = "0";
    closer.style.fontSize = "18px";
    closer.style.cursor = "pointer";
    closer.style.fontWeight = "bold";
    closer.style.padding = "0px";
    closer.style.backgroundColor = "transparent";
    closer.style.display = "block";
    closer.style.textAlign = "right";
    closer.style.zIndex = "1001";
    popupElement.appendChild(closer);

    const content = document.createElement("div");
    content.style.marginTop = "5px";
    popupElement.appendChild(content);

    const popupOverlay = new Overlay({
      element: popupElement,
      autoPan: true,
      autoPanAnimation: { duration: 250 },
      positioning: "bottom-center",
      stopEvent: true,
    });
    map.addOverlay(popupOverlay);

    const displayFeatureInfo = async (evt) => {
      if (!activeLayer) return;

      const viewResolution = map.getView().getResolution();
      const coordinate = evt.coordinate;

      const layers = map.getLayers().getArray();
      const wmsLayer = layers.find((layer) => layer.get("id") === activeLayer);
      const wmsSource = wmsLayer?.getSource();

      if (wmsSource instanceof ImageWMSSource && wmsSource.getFeatureInfoUrl) {
        const url = wmsSource.getFeatureInfoUrl(
          evt.coordinate,
          viewResolution,
          "EPSG:3857",
          { INFO_FORMAT: "application/json" }
        );

        if (url) {
          try {
            const response = await fetch(url);
            const data = await response.json();
            if (data.features && data.features.length > 0) {
              const attributes = data.features[0].properties;
              content.innerHTML = `
                <p><strong>${activeLayer}</strong></p>
                <ul>
                  ${Object.keys(attributes)
                    .map((key) => `<li><strong>${key}:</strong> ${attributes[key]}</li>`)
                    .join("")}
                </ul>
              `;
              popupOverlay.setPosition(coordinate);
            }
          } catch (error) {
            console.error("Error fetching feature info:", error);
          }
        }
      }
    };

    map.on("singleclick", displayFeatureInfo);

    closer.onclick = function () {
      popupOverlay.setPosition(undefined);
    };

    return () => {
      if (map) {
        map.removeOverlay(popupOverlay);
        map.un("singleclick", displayFeatureInfo);
      }
    };
  }, [map, activeLayer]);

  return (
    <div 
      style={{
        position: "absolute", // Membuat pencarian di posisi tetap
        top: "65px", // Atur posisi di sudut kiri atas
        left: "10px",
        zIndex: 1000, // Pastikan tetap di atas elemen peta
        backgroundColor: "white", // Menambahkan background agar kontras dengan peta
        padding: "10px",
        borderRadius: "4px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        width: "30%", 
      }}
    >
      <Autocomplete
        options={layerNames}
        value={searchTerm}
        clearOnEscape // Menambahkan fitur clear saat tekan tombol escape
        onChange={(event, newValue) => {
          if (newValue === null) {
            setSearchTerm(""); // Bersihkan pencarian saat tombol X ditekan
        } else {
            setSearchTerm(newValue);
            addLayerToMap(newValue);
        }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Cari Lokasi"
            variant="outlined"
            onChange={(e) => setSearchTerm(e.target.value)} // Perbarui term saat pengguna mengetik
          />
        )}
        style={{ width: "100%" }}
      />
    </div>
  );
};

export default LayerStunting;
